import React, { useContext, useEffect, useState, useRef } from "react";
import './CognitoOTPLogin.scss';
import { useHistory } from "react-router";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { countriesList, Country, defaultCountry } from "core/jsons/countries";
import { CognitoOTPAuthContext } from "cognito/CognitoOTPAuthContext";

const CognitoOTPLogin: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [timer, setTimer] = useState<number>(60);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);

  const { isAuthenticated, isInitialized, initiateLogin, login } = useContext(CognitoOTPAuthContext);
  const history = useHistory();

  // Create a ref for the OTP input field
  const otpInputRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null); // Ref to hold the interval ID

  const handleCountryChange = (countryCode: string) => {
    const country = countriesList.find(c => c.code === countryCode);
    if (country) {
      setSelectedCountry(country);
      setPhoneNumber(`${country.dial_code}${phoneNumber.replace(/^\+[\d\s]*/, '')}`.trim());
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value.trim());
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const cleanedPhone = phone.replace(/^\+|\s+/g, '');
    const isValid = /^\d+$/.test(cleanedPhone) && cleanedPhone.length >= 7;
    return isValid;
  };

  const validateOtp = (otp: string): boolean => {
    return /^\d{8}$/.test(otp); // Check if OTP is exactly 8 digits
  };

  const sendOtp = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!selectedCountry) {
      setErrorMessage("Please select a country.");
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setErrorMessage("Please enter a valid phone number.");
      return;
    }

    initiateLogin(phoneNumber).then(() => {
      setIsOtpSent(true);
      startTimer();
    })
      .catch((err: { message: string }) => {
        setErrorMessage(err.message);
      });
  }

  const resendOtp = () => {
    if (!isTimerActive) {
      initiateLogin(phoneNumber).then(() => {
        setIsOtpSent(true);
        startTimer();
      })
        .catch((err: { message: string }) => {
          setErrorMessage(err.message);
        });
    }
  };

  const signIn = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    login(otp)
      .then(() => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      })
      .catch((err: { message: string }) => {
        setErrorMessage(err.message);
      });
  };

  const startTimer = () => {
    setIsTimerActive(true);
    setTimer(60);

    intervalRef.current = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setIsTimerActive(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const onOTPChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers in the OTP input
    if (/^\d*$/.test(value) && value.length <= 8) {
      setOtp(value);
    }
  }

  useEffect(() => {
    if (phoneNumber) {
      const country = countriesList.find(c => phoneNumber.startsWith(c.dial_code));
      if (country) {
        setSelectedCountry(country);
      } else {
        setSelectedCountry(undefined);
      }
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (isInitialized && isAuthenticated) history.replace("/");
  }, [isInitialized, isAuthenticated]);

  useEffect(() => {
    fetch('https://ipinfo.io/json')
      .then(async (response) => {
        const data = await response.json();
        const country = countriesList.find(c => c.code === data.country);
        handleCountryChange(country ? data.country : defaultCountry.code);
      })
      .catch((error) => {
        console.error('IP Request failed:', error);
        handleCountryChange(defaultCountry.code);
      });
  }, []);

  // Focus on the OTP input field when OTP is sent
  useEffect(() => {
    if (isOtpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [isOtpSent]);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container p-3 w-100 login-container">
      <div className="d-flex w-100 justify-content-between">
        <div className="w-50 d-flex justify-content-center align-items-center flex-column">
          <img
            src={`${process.env.REACT_APP_WEB_API}/assets/images/logo_moc.png`}
            width="175px"
            alt="Logo"
          />
          {errorMessage && <div className="mt-4 text-danger">{errorMessage}</div>}
          <div className="mt-4 text-center fw-bold header">LOGIN</div>
          <div className="login-form">
            <form onSubmit={isOtpSent ? signIn : sendOtp} className="mt-4">
              <div className="form-group p-1">
                <label htmlFor="countrySelect">Country</label>
                <select
                  id="countrySelect"
                  className="form-control"
                  onChange={(e) => { handleCountryChange(e.target.value) }}
                  value={selectedCountry ? selectedCountry.code : ''}
                  required
                  disabled={isOtpSent}
                >
                  <option value="" disabled>Select Country</option>
                  {countriesList.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.dial_code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group p-1 mt-3">
                <label htmlFor="phoneNumber">Phone Number</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="phoneNumber"
                    placeholder="Enter your phone number"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    required
                    disabled={isOtpSent}
                  />
                </div>
              </div>
              {isOtpSent && (
                <div className="form-group p-1 mt-3">
                  <label htmlFor="otp">OTP</label>
                  <input
                    type="text"
                    className="form-control"
                    id="otp"
                    placeholder="Enter the OTP"
                    value={otp}
                    onChange={onOTPChange}
                    required
                    ref={otpInputRef}
                  />
                  <div className="mt-2">
                    <button type="button" className="btn btn-link" onClick={resendOtp} disabled={isTimerActive}>
                      Resend OTP
                    </button>
                    {isTimerActive && <span className="ml-2">{timer} seconds remaining</span>}
                  </div>
                </div>
              )}
              <div className="p-1 text-center mt-4">
                <button
                  type="submit"
                  className="btn btn-success btn-block w-100 sign-in-button"
                  disabled={!validateOtp(otp) && isOtpSent} // Disable button if OTP is invalid
                >
                  {isOtpSent ? "Login" : "Send OTP"}
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="d-flex justify-content-center align-items-center">
          <img
            src={`${process.env.REACT_APP_WEB_API}/assets/images/login.svg`}
            alt="Login Illustration"
          />
        </div>
      </div>
    </div>
  );
}

export default CognitoOTPLogin;